import React, { useEffect, useMemo, useState } from 'react';
import { User, Users, Loader2, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { managedPlayersApi } from '../../profile/services/managedPlayersApi';

/**
 * Who is this for — the payer, or one of the players they manage.
 *
 * A component rather than a step, because the same question is asked in more
 * than one place: the checkout page asks it first, and a list that has to hold
 * a place before payment asks it before it can hold anything. Two copies of
 * this question drifted apart once already — one knew how to rule out someone
 * who already had a place and the other did not.
 *
 * It fetches its own players. Every caller needs the same list, and a caller
 * that has to fetch it first can only get that wrong: the tournament flow
 * called a method that did not exist, swallowed the error, concluded the payer
 * managed nobody, and silently registered the wrong person.
 *
 * ─── PROPS ──────────────────────────────────────────────────────────────────
 *
 *   value       null for the payer themselves, otherwise a managed player's id
 *   onChange    (id | null) => void
 *   takenIds    who already holds a place, shown but not selectable
 *   heading     override for the question
 */
const displayName = (p) =>
  [p?.firstName || p?.first_name, p?.lastName || p?.last_name].filter(Boolean).join(' ')
  || p?.name || 'Managed player';

export default function PaymentRegistrantSelect({
  value = null,
  onChange,
  takenIds = [],
  provisionalIds = [],
  heading = 'Who is this for?',
  subheading = 'Choose yourself or one of the players you manage.',
}) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    // getAll, not list. /user?managedBy=me answers { data, pagination }; other
    // callers hand back a bare array. Accept both rather than guessing one.
    managedPlayersApi.getAll()
      .then((res) => {
        if (!live) return;
        setPlayers((Array.isArray(res) ? res : (res?.data || res?.players || [])).filter(Boolean));
        setFailed(false);
      })
      .catch(() => { if (live) setFailed(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const taken = useMemo(() => new Set(takenIds.map(String)), [takenIds]);
  /* Places held by THIS order rather than by a rival booking. Shown as taken
   * like any other — the payer really does hold the seat — but not a reason to
   * move the selection off them, because it is their order. */
  const ours = useMemo(() => new Set(provisionalIds.map(String)), [provisionalIds]);

  const options = useMemo(() => ([
    {
      id: null,
      label: 'My account',
      sub: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || null,
      Icon: User,
      disabled: taken.has(String(user?.id)),
      ours:     ours.has(String(user?.id)),
    },
    ...players.map(p => ({
      id: p.id,
      label: displayName(p),
      sub: 'Player you manage',
      Icon: Users,
      disabled: taken.has(String(p.userId ?? p.id)),
      ours:     ours.has(String(p.userId ?? p.id)),
    })),
  ]), [players, taken, ours, user]);

  /**
   * Never leave a disabled option selected — EXCEPT THIS ORDER'S OWN.
   *
   * The default is the payer, and on open play the payer is often the one
   * person who already has a place: checkout opened with a greyed row marked as
   * chosen, and Continue would have registered somebody who cannot be
   * registered. So the selection moves to the first person who actually can be.
   *
   * ⚠️ That move is wrong when the place is the one THIS order is holding, and
   * it is wrong in the most expensive direction. Book takes the seat before
   * checkout asks who it is for, so the payer holds a place on their own order
   * — and the move then slid the selection silently onto the first managed
   * player. An order raised for yourself settled for your child; the screen
   * said so, in small grey letters, on a step nobody re-reads.
   *
   * Those places arrive named as `provisional`, and are left alone. They still
   * show as taken, because they are: the honest screen says "you already have a
   * place here" AND keeps paying for it possible.
   *
   * Reported upward rather than held privately: the caller owns the answer, and
   * a selection the parent does not know about is one the summary and the Pay
   * step would disagree with.
   */
  useEffect(() => {
    if (loading) return;
    const current = options.find(o => String(o.id ?? 'self') === String(value ?? 'self'));
    if (current && !current.disabled) return;
    if (current?.ours) return;
    const firstFree = options.find(o => !o.disabled);
    // No one left is a real state — the step says so rather than silently
    // choosing nobody.
    if (firstFree) onChange?.(firstFree.id, firstFree.label);
  }, [loading, options, value]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* Not "everyone is disabled" but "there is nobody left to register" — the
   * place this order already holds is not a dead end, it is the thing being
   * paid for, so a checkout that consists solely of it is perfectly fine. */
  const nobodyAvailable = !loading && options.every(o => o.disabled && !o.ours);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 py-8 justify-center ${theme.textMuted}`}>
        <Loader2 size={16} className="animate-spin" />
        <span className="text-sm">Loading who you can register…</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className={`text-lg font-bold ${theme.text}`}>{heading}</h2>
        <p className={`text-sm mt-0.5 ${theme.textSecondary}`}>{subheading}</p>
      </div>

      {/* Said out loud rather than swallowed. A failed lookup used to leave the
          payer looking at a list of one and quietly registering themselves. */}
      {failed && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          Could not load the players you manage — you can still register yourself.
        </p>
      )}

      {nobodyAvailable && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          Everyone you can register already has a place here.
        </p>
      )}

      <div className="space-y-2">
        {options.map(({ id, label, sub, Icon, disabled, ours: isOurs }) => {
          const selected = String(value ?? 'self') === String(id ?? 'self');
          return (
            <button
              key={id ?? 'self'}
              type="button"
              disabled={disabled}
              data-testid={`registrant-${id ?? 'self'}`}
              onClick={() => onChange?.(id, label)}
              // Selection reads from the theme, not a hardcoded blue that
              // disappears against a cyan palette — and it is stated twice, by
              // the border and by a tick, because a one-pixel colour change is
              // not an answer somebody can see at a glance.
              className={`w-full text-left p-3 rounded-xl border-2 transition flex items-center gap-3 ${
                disabled ? `${theme.card} ${theme.border} opacity-50 cursor-not-allowed`
                         : selected ? `${theme.buttonSecondary} font-semibold`
                                    : `${theme.card} ${theme.border} ${theme.cardHover}`
              }`}
            >
              <Icon size={16} className={`flex-shrink-0 ${selected ? '' : theme.textMuted}`} />
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-medium ${theme.text}`}>{label}</span>
                <span className={`block text-xs ${selected ? '' : theme.textMuted}`}>
                  {/* Two different facts wear the same grey. Somebody else's
                      booking is a refusal; this order's own place is a receipt,
                      and saying "already has a place" over a tick with no
                      explanation reads as a screen contradicting itself. */}
                  {disabled
                    ? (isOurs ? 'Already booked — paying for it here' : 'Already has a place')
                    : sub}
                </span>
              </span>
              {selected && (
                <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${theme.primary}`}>
                  <Check size={12} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
